/**
 * Duel Elo Hook — Call after any duel is resolved to update Elo ratings.
 * Safe to call even if elo_ratings table doesn't exist yet (graceful fallback).
 */

import { processDuelResult } from './elo';
import { recomputeCareerStats } from './careerStats';
import { notifyDuelResult } from './notificationTriggers';
import { createLogger } from './logger';

const log = createLogger('DUEL_ELO');

/**
 * Process Elo and career stat updates after a duel result.
 * Non-blocking — errors are caught and logged.
 */
export async function onDuelCompleted(
  winnerId: string | null,
  loserId: string | null,
  duelId: string,
  challengerId?: string,
  challengedId?: string,
): Promise<void> {
  // Notify both players of the result (works for wins and draws)
  const playerA = challengerId ?? winnerId;
  const playerB = challengedId ?? loserId;
  if (playerA && playerB) {
    notifyDuelResult(playerA, playerB, duelId, winnerId).catch((err) =>
      log.warn('notifyDuelResult failed:', err),
    );
  }

  if (!winnerId || !loserId) {
    log.info('Draw or missing player, skipping Elo update');
    return;
  }

  try {
    const { winnerChange, loserChange } = await processDuelResult(
      winnerId,
      loserId,
      'overall',
      duelId,
    );

    log.info('Elo updated after duel', {
      duelId,
      winnerChange,
      loserChange,
    });
  } catch (err: any) {
    // Table might not exist yet — that's fine
    log.warn('Elo update failed (table may not exist):', err?.message);
  }

  // Update career stats for both players
  try {
    await Promise.all([
      recomputeCareerStats(winnerId),
      recomputeCareerStats(loserId),
    ]);
  } catch (err: any) {
    log.warn('Career stats update failed:', err?.message);
  }
}
